import * as React from "react";
import { cva } from "class-variance-authority";

import { cn } from "utils/cn";

export interface DescriptionListItem {
  label: React.ReactNode;
  /** `null`, `undefined`, `false` and blank strings render as an em dash. */
  value: React.ReactNode;
  /** Secondary line under the value. */
  hint?: React.ReactNode;
  /** 2 = spans two columns from the `sm` breakpoint up (ignored in a one-column list). */
  span?: 1 | 2;
  /** Monospace value, for ids, keys and URIs. */
  mono?: boolean;
}

const listVariants = cva("grid grid-cols-1 gap-x-6 gap-y-4", {
  variants: {
    columns: {
      1: "",
      2: "sm:grid-cols-2",
      3: "sm:grid-cols-2 lg:grid-cols-3",
    },
  },
  defaultVariants: { columns: 2 },
});

export interface DescriptionListProps extends React.HTMLAttributes<HTMLDListElement> {
  items: DescriptionListItem[];
  columns?: 1 | 2 | 3;
}

const isEmptyValue = (value: React.ReactNode): boolean =>
  value === null ||
  value === undefined ||
  value === false ||
  (typeof value === "string" && value.trim() === "");

/** Label / value pairs for detail panels (client profile, invoice header, settings). */
export const DescriptionList = React.forwardRef<HTMLDListElement, DescriptionListProps>(
  ({ items, columns = 2, className, ...props }, ref) => (
    <dl
      ref={ref}
      data-ui="description-list"
      className={cn(listVariants({ columns }), className)}
      {...props}
    >
      {items.map((item, index) => (
        <div
          key={index}
          className={cn("min-w-0", item.span === 2 && columns !== 1 && "sm:col-span-2")}
        >
          <dt className="text-xs text-muted-foreground">{item.label}</dt>
          <dd
            className={cn(
              "mt-1 break-words text-sm text-foreground",
              item.mono && "break-all font-mono"
            )}
          >
            {isEmptyValue(item.value) ? (
              <>
                <span aria-hidden="true" className="text-subtle-foreground">
                  —
                </span>
                <span className="sr-only">Not set</span>
              </>
            ) : (
              item.value
            )}
          </dd>
          {item.hint && <dd className="mt-1 text-xs text-muted-foreground">{item.hint}</dd>}
        </div>
      ))}
    </dl>
  )
);
DescriptionList.displayName = "DescriptionList";
